<div class="landing">
  <div class="landing__content">
    <!-- HERO / INTRO -->
    <header class="hero">
      <div class="hero-inner">
        <div class="hero-media">
          <img src="assets/mag-landing/image2.jpeg" alt="MAG landing screenshot" />
        </div>
        <div class="hero-copy">
          <h1 class="hero-title">MAG – Market Avenue Generator</h1>
          <p class="hero-subtitle">
            AI-supported decision support system for developing sustainable and market-ready business models
          </p>
          <p class="subtitle">
            The main goal is to enable users to develop clear, coherent and sustainability-oriented business models
            through a structured, methodology-based workflow. It supports the transition from an early idea to a
            well-defined and actionable business model by providing guidance, structured inputs and AI-supported
            feedback throughout the process.
          </p>
          <div class="hero-actions">
            <a class="btn btn-primary" routerLink="/login">Start your business model</a>
            <a
              class="btn btn-secondary"
              href="/radiant/bm-dss/assets/pdf/Radiant_MAG_User_Manual.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              User manual
            </a>
          </div>
        </div>
      </div>
    </header>

    <!-- KEY BENEFITS -->
    <section class="section section-benefits">
      <h2 class="section-title">Key benefits</h2>
      <div class="benefits-grid">
        <article class="benefit">
          <div class="benefit-icon-wrapper">
            <img
              src="assets/mag-landing/structured-workflows.png"
              alt="Structured workflow icon"
              class="benefit-icon"
            />
          </div>
          <h3 class="benefit-title">Structured workflow</h3>
          <p>
            Provides a clear, step-by-step process that guides users from an initial idea to a complete and coherent
            business model.
          </p>
        </article>

        <article class="benefit">
          <div class="benefit-icon-wrapper">
            <img
              src="assets/mag-landing/ai-supported-refinement.png"
              alt="AI-supported icon"
              class="benefit-icon"
            />
          </div>
          <h3 class="benefit-title">AI-supported</h3>
          <p>
            Real-time, AI-supported feedback that improves consistency and completeness throughout the development
            process.
          </p>
        </article>

        <article class="benefit">
          <div class="benefit-icon-wrapper">
            <img
              src="assets/mag-landing/market-ready.png"
              alt="Market-ready output icon"
              class="benefit-icon"
            />
          </div>
          <h3 class="benefit-title">Market-ready output</h3>
          <p>
            Ensures the business model aligns with real customer needs and value-chain conditions, increasing
            feasibility and long-term potential.
          </p>
        </article>
      </div>
    </section>

    <!-- MODULES -->
    <section class="section section-modules">
      <h2 class="section-title">MAG modules</h2>
      <div class="modules-grid">
        <article class="module">
          <div class="module-icon-wrapper">
            <img
              src="assets/mag-landing/lean-canvas.png"
              alt="Lean Canvas icon"
              class="module-icon"
            />
          </div>
          <h3 class="module-title">Lean Canvas</h3>
          <p>
            Provides a structured starting point for defining the core components of the business idea, including the
            problem, solution and customer segments.
          </p>
        </article>

        <article class="module">
          <div class="module-icon-wrapper">
            <img
              src="assets/mag-landing/trl-assessment.png"
              alt="TRL Assessment icon"
              class="module-icon"
            />
          </div>
          <h3 class="module-title">TRL Assessment</h3>
          <p>
            Helps determine the technological maturity of your innovation through a guided, nine-level scale,
            ensuring alignment between concept and feasibility.
          </p>
        </article>

        <article class="module">
          <div class="module-icon-wrapper">
            <img
              src="assets/mag-landing/business-model.png"
              alt="Business model icon"
              class="module-icon"
            />
          </div>
          <h3 class="module-title">Business model</h3>
          <p>
            Supports the development of a complete and coherent business model by detailing market positioning,
            operations, financial logic and key activities.
          </p>
        </article>

        <article class="module">
          <div class="module-icon-wrapper">
            <img
              src="assets/mag-landing/sustainability.png"
              alt="Sustainability icon"
              class="module-icon"
            />
          </div>
          <h3 class="module-title">Sustainability</h3>
          <p>
            Evaluates the economic, environmental and social impacts of your business model across selected
            value-chain nodes.
          </p>
        </article>

        <article class="module">
          <div class="module-icon-wrapper">
            <img
              src="assets/mag-landing/insights.png"
              alt="Insights icon"
              class="module-icon"
            />
          </div>
          <h3 class="module-title">Insights</h3>
          <p>
            Offers AI-supported comments and recommendations that highlight missing information, improve coherence
            and strengthen the overall quality of the business model.
          </p>
        </article>
      </div>
    </section>

    <!-- TARGET USERS -->
    <section class="section section-audience">
      <h2 class="section-title">Who is it for?</h2>
      <div class="audience-grid">
        <article class="audience">
          <div class="audience-icon-wrapper">
            <img
              src="assets/mag-landing/farmers.png"
              alt="Farmers icon"
              class="audience-icon"
            />
          </div>
          <h3 class="audience-title">Farmers</h3>
        </article>

        <article class="audience">
          <div class="audience-icon-wrapper">
            <img
              src="assets/mag-landing/smes-startups.png"
              alt="SMEs and start-ups icon"
              class="audience-icon"
            />
          </div>
          <h3 class="audience-title">SMEs and start-ups</h3>
        </article>

        <article class="audience">
          <div class="audience-icon-wrapper">
            <img
              src="assets/mag-landing/advisors-consultants.png"
              alt="Advisors and consultants icon"
              class="audience-icon"
            />
          </div>
          <h3 class="audience-title">Advisors and consultants</h3>
        </article>
      </div>
    </section>

    <!-- CTA (under target users) -->
    <section class="section section-cta">
      <h2 class="cta-title">Ready to build your business model?</h2>
      <a class="btn btn-primary cta-button" routerLink="/login">Start your business model</a>
    </section>

    <!-- PROJECT AND PARTNERS -->
    <section class="section section-project-partners">
      <h2 class="section-title">Project and partners</h2>
      <div class="project-partners-grid">
        <article class="project-card project-card--about">
          <p>
            <span class="project-name">RADIANT – Realising Dynamic Value Chains for Underutilised Crops</span> is a Horizon 2020 Research and Innovation
            Action project (GA No. 101000622), supporting the transition towards more diverse, resilient and sustainable
            agri-food systems in Europe.
          </p>
          <p>
            Its aim is to strengthen the role of underutilised crops by improving their integration into value chains,
            enhancing agrobiodiversity and supporting evidence-based innovation.
          </p>
          <p>
            RADIANT provides knowledge, data and practical methodologies developed through Dynamic Value Chain modelling,
            on-farm demonstrations and stakeholder co-creation, supporting more sustainable and market-responsive
            agri-food value chains.
          </p>
        </article>
        <article class="project-card project-card--partners">
          <p>
            MAG was developed by the Jozef Stefan Institute with project partners from Meta Circularity and the RADIANT
            project.
          </p>
          <div class="project-logos">
            <img src="assets/mag-landing/ijs.png" alt="Partner logo 1" class="project-logo" />
            <img src="assets/mag-landing/meta.png" alt="Partner logo 2" class="project-logo" />            
          </div>
        </article>
      </div>
    </section>
  </div>
</div>
